/**
 * useActiveRole — which role the user is currently using.
 *
 * One account can be both job_seeker and recruiter. The user's
 * `persona` (stored on the user record) is their PRIMARY identity;
 * `activeRole` is which mode they're currently working in. We persist
 * the choice in localStorage so they land on the right dashboard next
 * login.
 *
 * Resolution order on read:
 *   1. localStorage value (if present)
 *   2. user.persona from the auth context
 *   3. 'job_seeker' default
 */

import { useCallback, useEffect, useState } from 'react'
import { useAuth } from './useAuth'
import api from '../api/client'

export type ActiveRole = 'recruiter' | 'job_seeker'

const STORAGE_KEY = 'fth_active_role'


function readStored(): ActiveRole | null {
  if (typeof window === 'undefined') return null
  try {
    const v = window.localStorage.getItem(STORAGE_KEY)
    if (v === 'recruiter' || v === 'job_seeker') return v
  } catch {
    /* localStorage blocked — fall through */
  }
  return null
}


function writeStored(role: ActiveRole) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, role)
  } catch {
    /* ignore */
  }
}


export function useActiveRole() {
  const { user } = useAuth()
  // persona defaults to 'job_seeker' on the backend; treat anything else as recruiter
  const personaRole: ActiveRole = user?.persona === 'recruiter' ? 'recruiter' : 'job_seeker'

  const [activeRole, setActiveRoleState] = useState<ActiveRole>(() => {
    return readStored() ?? personaRole
  })

  // When the user object loads after a refresh, fall back to persona if
  // we never picked up a stored value (first render before localStorage).
  useEffect(() => {
    const stored = readStored()
    if (stored === null && user) {
      setActiveRoleState(personaRole)
    }
  }, [user, personaRole])

  const setActiveRole = useCallback((role: ActiveRole) => {
    writeStored(role)
    setActiveRoleState(role)
    // Switching INTO recruiter mode IS the "become a recruiter" transition:
    // persist persona server-side so backend recruiter gates agree with the UI
    // (otherwise submit 403s). Non-blocking; idempotent.
    if (role === 'recruiter' && user && user.persona !== 'recruiter') {
      api.becomeRecruiter().catch(() => { /* submit will re-gate if this fails */ })
    }
  }, [user])

  const toggleActiveRole = useCallback(() => {
    setActiveRole(activeRole === 'recruiter' ? 'job_seeker' : 'recruiter')
  }, [activeRole, setActiveRole])

  // canSwitchRole: anyone authenticated can see the toggle. We could
  // gate to users whose persona OR active subscription includes
  // recruiter, but for now we let any logged-in user flip — the
  // recruiter dashboard already gates batch features by plan tier.
  const canSwitchRole = Boolean(user)

  return {
    activeRole,
    setActiveRole,
    toggleActiveRole,
    canSwitchRole,
    isRecruiterMode: activeRole === 'recruiter',
    isJobSeekerMode: activeRole === 'job_seeker',
  }
}
